// System dependencies

import { AuthError } from "@supabase/supabase-js";

// Auth Enums ------------------------------------------------------------------------------------------------------------------------------

// Note that our naming convention is adopting snake_case to conform to the Supabase convention for the sake of consistency and readability
// through the Supabase ecosystem and it is considered bad form to mix cases in enum values. We'll use train-case for the friendly name
// and TOPT issuer.

export enum AuthFields {
  "Aal" = "aal",
  "AvatarUrl" = "avatar_url",
  "Challenge" = "challenge",
  "ChallengeId" = "challenge_id",
  "Code" = "code",
  "ConfirmPassword" = "confirm_password",
  "CreatedAt" = "created_at",
  "DisplayWarning" = "display_warning",
  "Email" = "email",
  "Error" = "error",
  "ErrorCode" = "error_code",
  "ErrorDescription" = "error_description",
  "ExpiresAt" = "expires_at",
  "FactorType" = "factor_type",
  "FirstName" = "first_name",
  "From" = "from",
  "FullName" = "full_name",
  "HasVerifiedFactor" = "has_verified_factor",
  "Html" = "html",
  "Id" = "id",
  "IssuedAt" = "issued_at",
  "LastName" = "last_name",
  "Message" = "message",
  "MfaStatus" = "mfa_status",
  "Otp" = "otp",
  "Password" = "password",
  "Provider" = "provider",
  "Secret" = "secret",
  "SessionId" = "session_id",
  "Signature" = "signature",
  "Status" = "status",
  "Subject" = "subject",
  "To" = "to",
  "Totp" = "totp",
  "TotpCode" = "totp_code",
  "Type" = "type",
  "UpdatedAt" = "updated_at",
  "Uri" = "uri",
  "User" = "user",
  "UserId" = "user_id",
  "UserTotpWarning" = "user_totp_warning",
}

export enum AuthAal {
  "Aal0" = "aal0",
  "Aal1" = "aal1",
  "Aal2" = "aal2",
}

export enum AuthChallengeMode {
  "eotp" = "eotp",
  "totp" = "totp",
}

export enum AuthChallengeStatus {
  "Failure" = "failure",
  "Idle" = "idle",
  "Pending" = "pending",
  "Success" = "success",
}

export enum AuthProviders {
  "Apple" = "apple",
  "Email" = "email",
  "Facebook" = "facebook",
  "Github" = "github",
  "Google" = "google",
  "Twitter" = "twitter",
}

export enum AuthStatus {
  "Authenticated" = "authenticated",
  "Idle" = "idle",
  "Loading" = "loading",
  "None" = "none",
  "Unauthenticated" = "unauthenticated",
}

export enum AuthTotpStatus {
  "Expired" = "expired",
  "None" = "none",
  "Unverified" = "unverified",
  "Verified" = "verified",
}

export enum AuthTotpViewState {
  "Enroll" = "enroll",
  "Error" = "error",
  "Loading" = "loading",
  "Unenroll" = "unenroll",
}

export enum AuthWaitingStatus {
  "Accepted" = "accepted",
  "Error" = "error",
  "Unknown" = "unknown",
  "Waiting" = "waiting",
}

export enum AuthWaitingType {
  "Oauth_google" = "oauth_google",
  "Recovery" = "recovery",
  "Signup" = "signup",
  "Unknown" = "unknown",
}

// Auth Constants --------------------------------------------------------------------------------------------------------------------------

export const AUTH_FRIENDLY_NAME = "next-supabase-auth";
export const AUTH_TOTP_COOKIE = "totp_cookie";
export const AUTH_TOTP_DEFAULT_SECRET = "default-secret";
export const AUTH_TOTP_EXPIRY_IN_MINUTES = 5;
export const AUTH_TOTP_FACTOR_TYPE = "totp";
export const AUTH_TOTP_ISSUER = "next-supabase-auth";

export const INITIAL_TOTP_STATE: AuthTotp = {
  [AuthFields.ChallengeId]: "",
  [AuthFields.Id]: "",
  [AuthFields.Secret]: "",
  [AuthFields.Uri]: "",
};

// Auth Supabase Interfaces ----------------------------------------------------------------------------------------------------------------

export interface AuthChallenge {
  [AuthFields.Error]: AuthError | string | null;
  [AuthFields.Id]: string | null;
  [AuthFields.Status]: AuthChallengeStatus;
}

export interface AuthCookieData {
  [AuthFields.ExpiresAt]: string;
  [AuthFields.IssuedAt]: string;
  [AuthFields.MfaStatus]: string;
  [AuthFields.SessionId]: string;
  [AuthFields.Signature]: string;
  [AuthFields.UserId]: string;
}

export interface AuthEmailOtpData {
  [AuthFields.CreatedAt]: string;
  [AuthFields.ExpiresAt]: string;
  [AuthFields.Id]: string;
  [AuthFields.Otp]: string;
  [AuthFields.UserId]: string;
}

export interface AuthEmailPayload {
  [AuthFields.From]?: string;
  [AuthFields.Html]: string;
  [AuthFields.Subject]: string;
  [AuthFields.To]: string;
}

export interface AuthResponse {
  [AuthFields.Id]?: string | null;
  [AuthFields.Message]: string;
  [AuthFields.Status]: number;
}

export interface AuthTotp {
  [AuthFields.ChallengeId]: string | null;
  [AuthFields.Id]: string | null;
  [AuthFields.Secret]: string | null;
  [AuthFields.Uri]: string | null;
}

export interface AuthTotpError {
  [AuthFields.Code]: string;
  [AuthFields.Message]: string | null;
}

// Auth User Interfaces --------------------------------------------------------------------------------------------------------------------

export interface AuthUser {
  [AuthFields.AvatarUrl]: string | null;
  [AuthFields.CreatedAt]: string | null;
  [AuthFields.Email]: string;
  [AuthFields.FirstName]: string | null;
  [AuthFields.FullName]: string | null;
  [AuthFields.Id]: string;
  [AuthFields.LastName]: string | null;
  [AuthFields.UpdatedAt]: string | null;
}

export interface AuthUserCrud {
  [AuthFields.AvatarUrl]: string | null;
  [AuthFields.FirstName]: string | null;
  [AuthFields.FullName]: string | null;
  [AuthFields.Id]: string;
  [AuthFields.LastName]: string | null;
  [AuthFields.UpdatedAt]: string | null;
}

// Auth Redux Interfaces -------------------------------------------------------------------------------------------------------------------

export interface AuthRedux {
  [AuthFields.Challenge]: AuthChallenge | null;
  [AuthFields.DisplayWarning]: boolean;
  [AuthFields.Error]: AuthError | string | null;
  [AuthFields.HasVerifiedFactor]: boolean;
  [AuthFields.Provider]: AuthProviders | null;
  [AuthFields.Status]: AuthStatus;
  [AuthFields.UserTotpWarning]: boolean;
  [AuthFields.User]: AuthUser | null;
}
